import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

export type Severity = 'info' | 'warning' | 'error' | 'critical'
export type FindingSource = 'docker' | 'kernel' | 'systemd'

export interface LogFinding {
  id: string
  fingerprint: string
  signature_short: string
  pattern_matched: string
  severity: Severity
  source: FindingSource
  sample_raw: string
  parsed_fields: Record<string, unknown>
  service_id: string | null
  stack: string | null
  node_id: string | null
  first_seen: string
  last_seen: string
  count_total: number
  silenced_until: string | null
  acknowledged_at: string | null
  resolved_at: string | null
}

export interface Occurrence {
  ts: string
  source: FindingSource
  raw_line: string | null
  context_path: string | null
}

export function useFindings(opts?: { activeOnly?: boolean; severity?: Severity | null }) {
  const qs = new URLSearchParams()
  if (opts?.activeOnly) qs.set('active_only', 'true')
  if (opts?.severity) qs.set('severity', opts.severity)
  const q = qs.toString()
  return useQuery<LogFinding[]>({
    queryKey: ['findings', opts],
    queryFn: () => api<LogFinding[]>(`/api/findings${q ? `?${q}` : ''}`),
    refetchInterval: 15_000,
  })
}

export function useFinding(id: string | undefined) {
  return useQuery<LogFinding>({
    queryKey: ['finding', id],
    queryFn: () => api<LogFinding>(`/api/findings/${id!}`),
    enabled: !!id,
  })
}

export function useOccurrences(id: string | undefined) {
  return useQuery<Occurrence[]>({
    queryKey: ['finding-occurrences', id],
    queryFn: () => api<Occurrence[]>(`/api/findings/${id!}/occurrences`),
    enabled: !!id,
  })
}

export function useAckFinding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/api/findings/${id}/ack`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['findings'] }),
  })
}

export function useSilenceFinding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, seconds }: { id: string; seconds: number }) =>
      api(`/api/findings/${id}/silence`, {
        method: 'POST',
        body: JSON.stringify({ duration_seconds: seconds }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['findings'] }),
  })
}
